import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const KEY = "klystic_product";
const ProductContext = createContext(null);

/**
 * Static, hand-maintained product metadata. Kept in sync with backend CHECKLISTS.
 * Colors are Tailwind class prefixes so consumers can compose classnames.
 */
export const PRODUCT_META = {
  keplerio: {
    key: "keplerio",
    name: "Keplerio",
    tagline: "Travel companion · Home + App",
    environment: "UAT",
    logo: "/assets/keplerio-logo.png",
    logoOnDark: "/assets/keplerio-logo-white.png",
    swoosh: "bg-gradient-to-r from-keplerio-gold via-[#7CC4C0] to-keplerio-teal",
    theme: {
      primary: "keplerio-navy",
      primaryDark: "keplerio-navy-dark",
      accent: "keplerio-gold",
      accentAlt: "keplerio-teal",
      ring: "keplerio-teal",
      textAccent: "keplerio-teal",
      badgeBg: "bg-keplerio-gold/30",
      badgeText: "text-keplerio-navy-dark",
      heroBg: "bg-keplerio-navy",
      heroText: "text-white",
    },
  },
  tarklen: {
    key: "tarklen",
    name: "Tarklen",
    tagline: "Connect. Automate. Accelerate.",
    environment: "SIT",
    logo: "/assets/tarklen-logo.png",
    theme: {
      primary: "tarklen-blue",
      primaryDark: "tarklen-blue-dark",
      accent: "tarklen-green",
      accentAlt: "tarklen-purple",
      ring: "tarklen-purple",
      textAccent: "tarklen-purple",
      badgeBg: "bg-tarklen-green/20",
      badgeText: "text-tarklen-green-dark",
      heroBg: "bg-gradient-to-br from-tarklen-blue via-tarklen-purple to-tarklen-green",
      heroText: "text-white",
    },
  },
};

export function ProductProvider({ children }) {
  const [product, setProductState] = useState(() => {
    try {
      const stored = localStorage.getItem(KEY);
      return stored && PRODUCT_META[stored] ? stored : null;
    } catch {
      return null;
    }
  });

  const setProduct = useCallback((key) => {
    if (!PRODUCT_META[key]) return;
    setProductState(key);
    try {
      localStorage.setItem(KEY, key);
    } catch { /* silent */ }
  }, []);

  const clearProduct = useCallback(() => {
    setProductState(null);
    try {
      localStorage.removeItem(KEY);
    } catch { /* silent */ }
  }, []);

  const meta = product ? PRODUCT_META[product] : null;

  // Reflect on <html data-product="..."> so global CSS can theme accordingly.
  useEffect(() => {
    document.documentElement.setAttribute("data-product", product || "none");
  }, [product]);

  const value = useMemo(
    () => ({ product, meta, setProduct, clearProduct, allProducts: Object.values(PRODUCT_META) }),
    [product, meta, setProduct, clearProduct]
  );

  return <ProductContext.Provider value={value}>{children}</ProductContext.Provider>;
}

export function useProduct() {
  const ctx = useContext(ProductContext);
  if (!ctx) throw new Error("useProduct must be used inside ProductProvider");
  return ctx;
}
